import type { Content } from "../types";

export const content: Content = {
  name: "Michelle",
  handle: "miceha",
  domain: "miceha.codes",
  role: "Junior Frontend Developer",
  location: "Germany",
  pronouns: "she/her",
  status: "Currently employed",
  joined: "2023-01",

  intro: [
    "I build interfaces for the web, and I'm slowly teaching myself what happens on the other side of the network tab.",
    "Mostly TypeScript and React with a little Node.js on the side. Currently looking for a junior frontend role, Koblenz-based, remote-friendly, or hybrid. Available from October 2026.",
  ],

  aboutLong: [
    "I'm Michelle — a junior frontend developer based near Koblenz. I started writing code seriously about 3 years ago, after my apprenticeship of poking at HTML, CSS and JavaScript. I haven't shipped anything big and I don't pretend to know more than I do, but I've seen enough of the web development world to know which parts I want to get good at.",
    "Most of what I do lives in the browser: React, TypeScript, CSS and a little bit of Node.js. I like to build things that are aesthetically pleasing but I also want to understand the underlying systems.",
    "Lately I'm trying to round myself out. The frontend is the part of a product I can see, but most of the interesting problems happen further back — data, caching, the way a request slowly turns into a render. So I'm learning more about the backend, and trying not to be intimidated by SQL.",
    "This site is the place I try to keep the small things I learn and the projects I'm trying to finish. It's a scrapbook, the beginning, kept in plain monospace.",
  ],

  about: [
    "I'm a junior developer based near Koblenz. I haven't shipped anything famous and I don't pretend to know more than I do — but I've seen enough to know what I want to get good at.",
    "This site is where I keep the small things I learn, the projects I'm trying to finish, and the half-formed opinions I'd defend if someone asked nicely.",
  ],

  nowLong: {
    intro:
      "Updated whenever I remember (last touched June 2026). This are the things I'm focused on right at this moment, not a curated career page.",
    sections: [
      {
        title: "work",
        items: [
          "Looking for a junior frontend role. Koblenz-based, remote-friendly, or hybrid.",
          "Available from October 2026.",
          "Up until then, I had been working with React and TypeScript for the past three years and managed a few smaller clients as part of my job.",
        ],
      },
      {
        title: "learning",
        items: [
          "Different technologies and frameworks, to get a better feel for the backend. Node.js, Express.js, SQLite, and a little bit of PHP.",
          "Reading few pages at a time. It's slow. That's fine.",
          "Scrolling through different websites and tools to see how they look and function. To learn how to design things that are actually usable, and to see how other people solve the same problems I have.",
        ],
      },
      {
        title: "making",
        items: [
          "Write my personal website, just for fun.",
          "Rewriting my final project of my apprenticeship, to see how much I've improved and also improve the project itself.",
          "Trying to finish smaller projects that have been sitting in my drafts for months. I have a lot of them.",
        ],
      },
      {
        title: "reading",
        items: [
          "The Design of Everyday Things · Don, Norman",
          "Don't make me think! · Krug, Steve",
          "Laws of UX · Yablonski, Jon",
        ],
      },
      {
        title: "life",
        items: [
          "Near Koblenz in a small village. Trying to convince myself to get a driver's license.",
          "Energy drinks. Long walks. Two plants I haven't killed yet and supervising my cat that gets dangerously close to them.",
        ],
      },
    ],
    updatedAt: "22-06-2026",
  },

  stack: [
    "HTML5",
    "CSS3",
    "Sass/SCSS",
    "JavaScript",
    "TypeScript",
    "React",
    "Next.js",
    "Webpack",
    "Vite",
    "styled-components",
    "CSS Modules",
    "Tailwind",
    "Bootstrap",
    "Storybook",
    "Electron",
    "First Spirit",
    "Figma",
    "Jira",
    "Azure DevOps",
    "Git",
    "Node.js",
    "npm/yarn",
    "Accessibility",
    "Performance",
    "SEO",
    "PHP *",
    "SQL/DB *",
    "Magnolia *",
    "Pimcore *",
    "Vitest *",
    "Playwright *",
    "Cypress *",
    "Postman *",
    "BrowserStack *",
    "Jenkins *",
    "SonarQube *",
    "Google Analytics *",
    "Consentmanager *",
  ],

  notes: [
    {
      key: "electron-three-weeks",
      title: "Three weeks with Electron, and what I'd do differently",
      date: "2026-04-12",
      reading: "6 min",
      tag: "electron",
      excerpt:
        "I shipped a desktop app in three weeks using a stack I picked because it was familiar. Here's what surprised me, what I'd keep, and what I'd swap if I started again on a Monday.",
      body: [
        "I built tabby — a sticky-notes desktop app — over three weeks of evenings. The technical choice was Electron + React + TypeScript, picked entirely because I already knew React and I wanted to ship.",
        "The first surprise was how little Electron actually is. Almost everything I built was just React. The Electron-specific parts — windows, system tray, global shortcut, IPC — fit into about 80 lines across two files. The other 5,000 lines were a normal React app.",
        "The second surprise was how much friction there is in turning a normal React app into a desktop app that feels native. Drag-handles, traffic-light placement, dark-mode-following-the-system: each is fifteen minutes of googling and an hour of fiddling. None of it is hard. All of it is exactly the kind of work that doesn't look impressive in a pull request.",
        "If I started again today, I'd reach for Tauri. Mostly because I've never used it and I'm curious, partly because I'd like to ship something that doesn't bundle Chromium. The cost would be losing some of the React ecosystem assumptions; I think I'd survive.",
        "Things I'd keep: the React stack, IndexedDB for persistence (it's overkill, but the API is finally not awful), and the discipline of writing the smallest possible Electron surface so the app stays portable.",
      ],
    },
    {
      key: "css-switching",
      title: "Why I keep switching between CSS modules and styled-components",
      date: "2026-03-28",
      reading: "4 min",
      tag: "css",
      excerpt:
        "An ongoing, possibly never-ending personal debate. A few honest observations from someone who's tried to live with both for six months at a time.",
      body: [
        "I keep going back and forth between CSS modules and styled-components, and at this point I think the answer is that I should stop trying to pick a winner.",
        "CSS modules give me the thing I want most from styling: a stylesheet that's a stylesheet. I can read it. I can scan it. The class name in the JSX is just a string. I find it easier to reason about layout when the styles are visibly elsewhere.",
        "Styled-components give me the other thing I want: prop-driven variants and the kind of inline conditional styling that's tedious in plain CSS. When I'm building a component library, I miss them within twenty minutes.",
        "What I've stopped doing is treating this as a project-wide decision. Some components are clearly styled-components shaped. Some pages are clearly stylesheet-shaped. I let them coexist, and I move on.",
      ],
    },
    {
      key: "use-sync-external-store",
      title: "TIL: useSyncExternalStore exists, and it's kind of beautiful",
      date: "2026-03-04",
      reading: "3 min",
      tag: "react",
      excerpt:
        "I went five hooks deep before I noticed the sixth, and now I can't unsee it. Notes on subscribing to anything that isn't React from inside React.",
      body: [
        "I'd been subscribing to external state — localStorage, the window's online status, a media query — with useState + useEffect for months. It worked. It also had a tiny tearing problem during React 18's concurrent renders that I didn't really understand.",
        "useSyncExternalStore exists for this exact case. You give it a subscribe function and a snapshot function. It calls the snapshot when React needs the value, and re-renders the component when the subscription fires. That's it.",
        "I rewrote my four little subscription hooks in an afternoon. The code is shorter. The behaviour is correct. I have nothing to add.",
      ],
    },
    {
      key: "learning-backend",
      title:
        "Notes on learning the backend, from someone who used to be afraid of it",
      date: "2026-02-17",
      reading: "7 min",
      tag: "backend",
      excerpt:
        "Three months into actually trying. A list of things that turned out to be less scary than I'd assumed, and one that turned out to be more.",
      body: [
        "I avoided the backend for embarrassingly long. Not because anyone told me to, but because every time I peeked at it the words got bigger and the examples got more abstract. Databases. Migrations. Indexes. Caching strategies. Eventual consistency.",
        "Three months into actually trying, here's what's turned out to be less scary than I assumed: setting up Postgres locally, writing CRUD endpoints, picking an ORM (any one), reading a query plan if you take your time. None of these required a course. They required forty minutes and a real problem.",
        "Here's what turned out to be more scary: background jobs. Anything that runs not-when-someone-clicks-a-thing introduces a category of problem I didn't have on the frontend. What happens when it fails? What happens when it runs twice? What happens when the database is down? These questions are real questions, and I do not yet have good instincts for them.",
        "I'm not in a hurry. I'd rather understand one thing properly than ship something I can't reason about.",
      ],
    },
    {
      key: "useeffect-bugs",
      title: "A tiny taxonomy of useEffect bugs I've shipped",
      date: "2026-01-30",
      reading: "5 min",
      tag: "react",
      excerpt:
        "A short, honest field guide to the bugs I've personally introduced with useEffect, sorted by approximate embarrassment.",
      body: [
        "I've been writing React for about two years. In that time I've shipped, by my count, six different categories of useEffect bug. Some of them shipped to actual users. I will list them here in approximate order of embarrassment, smallest first.",
        "1. Missing deps. The classic. Lint catches most. The ones it doesn't are the closures-over-stale-state ones, which catch me about once a quarter.",
        "2. Effect that fires on every render. Usually because I forgot the deps array entirely, or because the dep I added is a freshly-created object literal.",
        "3. Race condition. Two effects, one network. The slower request comes back first and overwrites the newer one. I now have a small mental rule: every effect that does async work needs a cleanup that cancels.",
        "4. Effect that should have been a derived value. I now ask 'is this state, or is it a thing I can compute?' before I write the useState.",
        "5. Effect that should have been an event handler. The reactivity is in the wrong place; the effect runs when state changes, when really I wanted it to run when a button is clicked.",
        "6. Effect that subscribes to something React already handles. This is where useSyncExternalStore lives. See the note from last month.",
      ],
    },
    {
      key: "small-tools",
      title: "In praise of small tools, and the people who keep making them",
      date: "2026-01-08",
      reading: "4 min",
      tag: "meta",
      excerpt:
        "A short defence of the kind of software that does one thing, in a folder, and does not want your email address.",
      body: [
        "There's a particular kind of software I love: small tools, written by one person, hosted on a domain that probably belongs to them, that do exactly one thing and do not want your email address.",
        "I have a folder of them. A unit converter. A regex tester. A JSON formatter that doesn't fight me. A markdown previewer with three options and a sensible default for each. None of them have a roadmap, a Discord, or a paid tier.",
        "These tools are the reason I started building my own little things and writing them up here. I don't think every piece of software has to scale, or sell, or compound. Some of it can just exist, and be useful, and that's the whole point.",
      ],
    },
  ],

  bookmarks: [
    { title: "Designing Data-Intensive Applications", source: "Kleppmann, M." },
    { title: "patterns.dev", source: "patterns.dev" },
    { title: "You Don't Need a Build Step", source: "jvns.ca" },
    { title: "Refactoring UI", source: "Wathan & Schoger" },
  ],

  links: [
    { label: "linkedin", href: "/michellecelinahahn" },
    { label: "github", href: "@llamate" },
    { label: "email", href: "hi@miceha.codes" },
  ],

  contact: {
    intro:
      "The fastest way to reach me is email. I read everything. I reply to almost everything, usually within a couple of days. If I haven't replied in a week, please poke me — it's not personal, it's just that my inbox has feelings.",
    questions: [
      {
        q: "Are you looking for work?",
        a: "Yes, from May 2026. Junior frontend, or junior fullstack if I get to lean on frontend while I learn the rest. Berlin-based, remote-friendly, or hybrid.",
      },
    ],
  },
  now: [],
};
